// ============================================================
// SURFACE TRAITS - what ground can lend the person standing on it.
//
// A surface's `traits` are spread straight onto the projected `Target`
// (see `projectDefenderFrom`), so every entry here MUST be a real field
// on `Target` and must already mean something to the engine. A trait the
// combat code never reads would show on the sheet and do nothing, which
// is worse than not offering it.
//
// The immunity half is GENERATED from `IMMUNITY_MAP` rather than typed
// out, so it cannot drift from the traits the rest of the app honours.
// Everything else is listed explicitly, with the field the engine reads
// beside it.
//
// Pure data. Zero React dependency.
// ============================================================
import { IMMUNITY_MAP } from '../conditionEligibility';
import { COND_NAMES } from '../conditions';

export interface SurfaceTrait {
  /** The `Target` field written. */
  field: string;
  label: string;
  /** What it does, shown on hover. */
  hint: string;
  /** Numeric traits carry a value, not a flag - Machine's AP, and so on. */
  numeric?: boolean;
  /** What a numeric trait is set to when it is switched on. */
  preset?: number;
}

export interface SurfaceTraitGroup {
  id: string;
  title: string;
  traits: SurfaceTrait[];
}

/**
 * Traits that change how ground is crossed.
 *
 * The interesting and slightly circular group: these are exactly the
 * traits that decide whether ground catches you at all. `surfaceEffects`
 * resolves it - a surface never exempts you from itself.
 */
export const MOVEMENT_TRAITS: SurfaceTrait[] = [
  { field: 'flyer', label: 'Flyer', hint: 'Terrain stops mattering - walls, going and zones alike' },
  { field: 'hoverer', label: 'Hoverer', hint: 'Skims the ground: the COST stops mattering, walls do not' },
  { field: 'burrower', label: 'Burrower', hint: 'May travel through earth, below the ground plane' },
  { field: 'incorporeal', label: 'Incorporeal', hint: 'Passes through solid matter, and is barely there to hit' },
];

/** Traits that keep a body going, or make it harder to put down. */
const BODY_TRAITS: SurfaceTrait[] = [
  {
    field: 'regeneration',
    label: 'Regeneration',
    hint: 'Heals this many wounds at the start of each turn - a bio-mass, a med-bay floor',
    numeric: true,
    preset: 1,
  },
  {
    field: 'naturalArmour',
    label: 'Natural Armour',
    hint: 'This much armour on every location, over what they are wearing - encasing mud, ice, crust',
    numeric: true,
    preset: 2,
  },
  {
    field: 'daemonicProtection',
    label: 'Daemonic',
    hint: 'A daemonic save at this rating - ground the warp has a claim on',
    numeric: true,
    preset: 4,
  },
  { field: 'trueGrit', label: 'True Grit', hint: 'Critical damage is reduced by the toughness bonus' },
];

/** Traits about what standing here makes of you, to others. */
const PRESENCE_TRAITS: SurfaceTrait[] = [
  {
    field: 'fearRating',
    label: 'Fear',
    hint: 'Standing here makes THEM frightening at this rating - an altar, a throne of skulls',
    numeric: true,
    preset: 1,
  },
];

/**
 * Every trait a surface may lend, grouped as a GM would look for them.
 *
 * The first group is generated: every immunity source the app already
 * knows, described by what it makes you immune to.
 */
export function surfaceTraitGroups(): SurfaceTraitGroup[] {
  return [
    {
      id: 'immunity',
      title: 'Immune while they stand on it',
      traits: IMMUNITY_MAP.map((source) => ({
        field: source.field,
        label: source.label,
        hint: `Immune to ${source.conditions.map((c) => COND_NAMES[c] || c).join(', ')}`,
        numeric: source.isNumeric,
        // Machine is a NUMBER - its AP. A flag written there would give a
        // combatant an armour value of `true`.
        preset: source.isNumeric ? 4 : undefined,
      })),
    },
    { id: 'body', title: 'Body and recovery', traits: BODY_TRAITS },
    { id: 'presence', title: 'What it makes of them', traits: PRESENCE_TRAITS },
    { id: 'movement', title: 'How they cross ground', traits: MOVEMENT_TRAITS },
  ];
}

/** Every trait, flat - for lookups by field. */
export function allSurfaceTraits(): SurfaceTrait[] {
  return surfaceTraitGroups().flatMap((g) => g.traits);
}

/** The value written when a trait is switched on. */
export function traitOnValue(trait: SurfaceTrait): boolean | number {
  return trait.numeric ? (trait.preset ?? 1) : true;
}

/**
 * Add a condition code to a surface, replacing anything it supersedes.
 *
 * A surface is a DESCRIPTION of ground, not a log of what a GM clicked.
 * Picking Fatigue 2 and then Fatigue 3 meant the ground granted both, and
 * same-type conditions stack additively - so the mire quietly imposed
 * Fatigue 5 (v7.78.0). The last choice wins, which is what picking again
 * means everywhere else in this app.
 *
 * Same-scope custom modifiers replace too: "Agility −10" then "Agility
 * −20" is a correction, not two penalties. Different scopes, and
 * per-location conditions, stay side by side - those are genuinely
 * different things.
 */
export function mergeSurfaceGrant(grants: string[], code: string): string[] {
  const slot = grantSlot(code);
  const kept = grants.filter((g) => grantSlot(g) !== slot);
  return [...kept, code];
}

/** What two codes must share to be the same entry on a surface. */
function grantSlot(code: string): string {
  if (code.startsWith('Pen:')) {
    // Scope, not value: 'Pen:agi=-10' and 'Pen:agi=-20' are one entry.
    const body = code.slice(4).split('=')[0];
    return `Pen:${body}`;
  }
  // An exposure by its NAME (v8.356.0): picking Painted again replaces
  // the Painted this ground already grants, whatever its numbers.
  if (code.startsWith('Ex:')) return `Ex:${code.slice(3).split(';')[0]}`;
  // Location-specific conditions keep their location - a crippled left
  // arm and a crippled head are two injuries, not one changed twice.
  const loc = code.match(/^(Cr|De):(.+)$/);
  if (loc) return code;
  const m = code.match(/^([A-Z][a-z]*)/);
  return m ? m[1] : code;
}
